import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box, Paper, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Divider, Alert, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, useTheme, alpha,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { useVenue } from '../../../../context/VenueContext';
import wineListItemService from '../../../../services/wineListItemService';
import inventoryItemService from '../../../../services/inventoryItemService';
import WineSearchBox from '../../../../components/venue/WineSearchBox';
import type { WineSearchResult } from '../../../../components/venue/WineSearchBox';
import CreateWineModal from '../../../../components/venue/CreateWineModal';
import type { Wine } from '../../../../services/wineService';
import type { WineListItemVisibility } from '../../../../types/venue';
import type { InventoryItemWithBalance } from '../../../../services/inventoryItemService';

interface FormData {
  listPrice: string;
  currency: string;
  binNumber: string;
  visibility: WineListItemVisibility;
  notes: string;
  bottleSize: string;
}

const WineListItemForm: React.FC = () => {
  const navigate = useNavigate();
  const { wineListId, itemId } = useParams<{ wineListId: string; itemId: string }>();
  const theme = useTheme();
  const { venueId, defaultCurrency } = useVenue();
  const isEdit = Boolean(itemId);
  const backPath = `/admin/venues/${venueId}/wine-lists/${wineListId}`;

  const [selectedWine, setSelectedWine] = useState<WineSearchResult | null>(null);
  const [wineError, setWineError] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalQuery, setCreateModalQuery] = useState('');

  // For edit mode: display the wine name read-only
  const [editWineDisplay, setEditWineDisplay] = useState('');

  // Inventory item link
  const [linkedItem, setLinkedItem] = useState<InventoryItemWithBalance | null>(null);
  const [itemSearchResults, setItemSearchResults] = useState<InventoryItemWithBalance[]>([]);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);

  // Confirmation dialog when selected item's bottleSize differs from existing
  const [confirmPending, setConfirmPending] = useState<InventoryItemWithBalance | null>(null);

  // Tracks that the loaded item has an inventoryItemId — locks bottleSize before async fetch completes
  const [editHasLinkedItemId, setEditHasLinkedItemId] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    listPrice: '',
    currency: defaultCurrency,
    binNumber: '',
    visibility: 'LIVE',
    notes: '',
    bottleSize: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(isEdit);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  // ── Load existing item in edit mode ──────────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !itemId || !wineListId) return;
    setLoading(true);
    wineListItemService.getItem(venueId, wineListId, itemId)
      .then(item => {
        setEditWineDisplay(item.wineDisplay?.name ?? item.wineId);
        setFormData({
          listPrice: item.listPrice != null ? String(item.listPrice) : '',
          currency: item.currency ?? defaultCurrency,
          binNumber: item.binNumber ?? '',
          visibility: item.visibility,
          notes: item.notes ?? '',
          bottleSize: item.bottleSize != null ? String(item.bottleSize) : '',
        });
        if (item.inventoryItemId) {
          setEditHasLinkedItemId(true);
          inventoryItemService.getItem(venueId, item.inventoryItemId)
            .then(inv => {
              const invItem = inv as unknown as InventoryItemWithBalance;
              setLinkedItem(invItem);
              // Sync displayed bottleSize with inventory item — corrects any DB drift
              if (invItem.bottleSize != null) {
                setFormData(prev => ({ ...prev, bottleSize: String(invItem.bottleSize) }));
              }
            })
            .catch(() => { /* non-blocking */ });
        }
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, [isEdit, itemId, wineListId, venueId, defaultCurrency]);

  // ── Debounced inventory item search ──────────────────────────────────────────
  const runItemSearch = useMemo(
    () => debounce(async (q: string) => {
      if (!q.trim()) { setItemSearchResults([]); setItemSearchLoading(false); return; }
      setItemSearchLoading(true);
      try {
        const res = await inventoryItemService.getItems(venueId, 1, 20, 'wineDisplay.name', 'asc', q);
        setItemSearchResults(res.data ?? []);
      } catch {
        setItemSearchResults([]);
      } finally {
        setItemSearchLoading(false);
      }
    }, 350),
    [venueId],
  );
  useEffect(() => () => runItemSearch.cancel(), [runItemSearch]);

  // ── Inventory item selection ───────────────────────────────────────────────────
  const applyInventoryItem = (item: InventoryItemWithBalance) => {
    setLinkedItem(item);
    setFormData(prev => ({ ...prev, bottleSize: item.bottleSize != null ? String(item.bottleSize) : '' }));
  };

  const handleInventoryItemSelect = (item: InventoryItemWithBalance | null) => {
    if (!item) {
      setLinkedItem(null);
      return;
    }
    const currentSize = formData.bottleSize ? parseFloat(formData.bottleSize) : null;
    const newSize = item.bottleSize ?? null;
    // Warn only when sizes actively differ (both non-null and not equal)
    if (currentSize != null && newSize != null && currentSize !== newSize) {
      setConfirmPending(item);
    } else {
      applyInventoryItem(item);
    }
  };

  // ── Generic field change ──────────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!isEdit && !selectedWine) {
      setWineError('Please select a wine.');
      return false;
    }
    if (formData.listPrice && isNaN(parseFloat(formData.listPrice))) {
      newErrors.listPrice = 'Must be a valid number.';
    }
    if (formData.bottleSize && isNaN(parseFloat(formData.bottleSize))) {
      newErrors.bottleSize = 'Must be a valid number.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!wineListId) return;
    setSaving(true);
    setApiError('');
    try {
      const listPrice = formData.listPrice ? parseFloat(formData.listPrice) : undefined;
      const bottleSize = formData.bottleSize ? parseFloat(formData.bottleSize) : undefined;

      if (isEdit && itemId) {
        await wineListItemService.updateItem(venueId, wineListId, itemId, {
          listPrice,
          currency: formData.currency.trim() || undefined,
          binNumber: formData.binNumber.trim() || undefined,
          visibility: formData.visibility,
          notes: formData.notes.trim() || undefined,
          bottleSize: bottleSize ?? undefined,
          // null → set: include inventoryItemId only when linking during this edit session
          ...(!editHasLinkedItemId && linkedItem ? { inventoryItemId: linkedItem._id } : {}),
        });
      } else {
        await wineListItemService.createItem(venueId, wineListId, {
          wineId: selectedWine!._id,
          ...(linkedItem && { inventoryItemId: linkedItem._id }),
          listPrice,
          currency: formData.currency.trim() || undefined,
          binNumber: formData.binNumber.trim() || undefined,
          visibility: formData.visibility,
          source: 'MANUAL',
          notes: formData.notes.trim() || undefined,
          active: true,
          ...(bottleSize != null && { bottleSize }),
        } as Parameters<typeof wineListItemService.createItem>[2]);
      }
      navigate(backPath);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'WINE_ALREADY_ON_LIST') {
        setApiError('This wine is already on this list. To add a different format, link a specific inventory item.');
      } else {
        setApiError(err?.response?.data?.message ?? 'Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Label for inventory item in autocomplete ───────────────────────────────────
  // wineDisplay.name already contains producer+wine+vintage — do NOT prepend producerName
  const inventoryItemLabel = (item: InventoryItemWithBalance) => {
    const name = item.wineDisplay?.name ?? item._id;
    const size = item.bottleSize != null
      ? (item.bottleSize >= 1000 ? `${item.bottleSize / 1000}L` : `${item.bottleSize}ml`)
      : null;
    return size ? `${name} · ${size}` : name;
  };

  const bottleSizeLocked = Boolean(linkedItem) || editHasLinkedItemId;

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadFailed) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, alignSelf: 'flex-start' }}>
          Wine List
        </Button>
        <Alert severity="error">
          Could not load this entry. It may have been removed. Go back and try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Wine List
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>{isEdit ? 'Edit Entry' : 'Add Wine to List'}</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {apiError && <Alert severity="error" sx={{ borderRadius: 1 }}>{apiError}</Alert>}

          {/* Wine selection */}
          {isEdit ? (
            <TextField
              label="Wine"
              value={editWineDisplay}
              fullWidth
              disabled
              helperText="Wine cannot be changed after adding. Remove and re-add to change the wine."
            />
          ) : (
            <>
              <WineSearchBox
                value={selectedWine}
                onChange={wine => { setSelectedWine(wine); setWineError(''); }}
                onCreateNew={query => { setCreateModalQuery(query); setCreateModalOpen(true); }}
                error={Boolean(wineError)}
                helperText={wineError || undefined}
                label="Wine"
              />
              {selectedWine && (
                <Box sx={{ display: 'flex', gap: 2, px: 0.25 }}>
                  {selectedWine.wineDisplay?.producerName && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.68rem' }}>Producer</Typography>
                      <Typography variant="body2">{selectedWine.wineDisplay.producerName}</Typography>
                    </Box>
                  )}
                  {selectedWine.wineDisplay?.regionName && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.68rem' }}>Region</Typography>
                      <Typography variant="body2">{selectedWine.wineDisplay.regionName}</Typography>
                    </Box>
                  )}
                  {selectedWine.displayVintage && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.68rem' }}>Vintage</Typography>
                      <Typography variant="body2">{selectedWine.displayVintage}</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}

          {/* Inventory item link */}
          <Autocomplete
            size="small"
            options={itemSearchResults}
            value={linkedItem}
            loading={itemSearchLoading}
            filterOptions={x => x}
            getOptionLabel={inventoryItemLabel}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            disabled={isEdit && Boolean(linkedItem || editHasLinkedItemId)}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') runItemSearch(value);
              if (reason === 'clear') { setLinkedItem(null); setItemSearchResults([]); }
            }}
            onChange={(_, val) => handleInventoryItemSelect(val)}
            renderOption={(props, opt) => (
              <Box component="li" {...props} key={opt._id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{inventoryItemLabel(opt)}</Typography>
                  {opt.balance != null && (
                    <Typography variant="caption" color="text.secondary">{opt.balance.quantity} in stock</Typography>
                  )}
                </Box>
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Linked Inventory Item (optional)"
                placeholder="Search to link a specific inventory item…"
                helperText={isEdit && Boolean(linkedItem || editHasLinkedItemId)
                  ? 'Inventory item link cannot be changed after creation.'
                  : 'Links this entry to a tracked inventory item. Allows the same wine in different formats on the same list.'}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {itemSearchLoading && <CircularProgress size={14} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />

          {/* Price + Currency + Bottle Size + Bin */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="List Price"
              name="listPrice"
              value={formData.listPrice}
              onChange={handleInputChange}
              error={Boolean(errors.listPrice)}
              helperText={errors.listPrice}
              type="number"
              sx={{ flex: 2, minWidth: 120 }}
              slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
            />
            <TextField
              label="Currency"
              name="currency"
              value={formData.currency}
              onChange={handleInputChange}
              sx={{ width: 110 }}
              slotProps={{ htmlInput: { maxLength: 3, style: { textTransform: 'uppercase' } } }}
            />
            <TextField
              label="Bottle Size (ml)"
              name="bottleSize"
              value={formData.bottleSize}
              onChange={handleInputChange}
              error={Boolean(errors.bottleSize)}
              helperText={bottleSizeLocked ? 'Set by linked inventory item' : errors.bottleSize}
              type="number"
              sx={{ width: 150 }}
              placeholder="e.g. 750"
              disabled={bottleSizeLocked}
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
            />
            <TextField
              label="Bin Number"
              name="binNumber"
              value={formData.binNumber}
              onChange={handleInputChange}
              sx={{ flex: 1, minWidth: 100 }}
              placeholder="e.g. C-14"
            />
          </Box>

          {/* Visibility */}
          <FormControl sx={{ maxWidth: 260 }}>
            <InputLabel>Visibility</InputLabel>
            <Select
              value={formData.visibility}
              label="Visibility"
              onChange={e => setFormData(prev => ({ ...prev, visibility: e.target.value as WineListItemVisibility }))}
            >
              <MenuItem value="LIVE">Live — visible on list</MenuItem>
              <MenuItem value="HIDDEN">Hidden — not shown</MenuItem>
            </Select>
          </FormControl>

          {/* Notes */}
          <TextField
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={3}
            placeholder="Tasting notes or list copy (optional)"
          />

          {/* Actions */}
          <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 140 }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? 'Save Changes' : 'Add to List'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Bottle size mismatch confirmation */}
      <Dialog open={Boolean(confirmPending)} onClose={() => setConfirmPending(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Update bottle size?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The selected inventory item has a different bottle size
            {confirmPending?.bottleSize != null && ` (${confirmPending.bottleSize}ml)`}.
            This will replace the current bottle size on this wine list entry.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmPending(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (confirmPending) applyInventoryItem(confirmPending);
              setConfirmPending(null);
            }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <CreateWineModal
        open={createModalOpen}
        initialQuery={createModalQuery}
        onCreated={(wine: Wine) => {
          const asSearchResult: WineSearchResult = {
            _id: wine._id,
            name: wine.name,
            displayVintage: wine.displayVintage,
            vintageType: wine.vintageType,
            wineDisplay: {
              producerName: wine.wineName?.producer?.name,
              regionName: wine.wineName?.region?.name,
              color: wine.wineName?.color,
            },
          };
          setSelectedWine(asSearchResult);
          setWineError('');
          setCreateModalOpen(false);
        }}
        onClose={() => setCreateModalOpen(false)}
      />
    </Box>
  );
};

export default WineListItemForm;
